import { useCallback, useMemo } from 'react';

import { useApplicationConfig } from '~/data/applicationConfig';
import { Enum, EnumListKey, useAllEnums } from '~/data/enums';
import {
  ProposalStatus,
  SalaryProposalData,
  SalaryProposalProps,
} from '~/data/proposals';
import { useYupValidationResolver } from '~/hooks/useYupValidationResolver';
import {
  CommonProposalFieldProps,
  EmployeeProposalDecision,
  EmployeeProposalUpdateCallback,
  UseCommonProposalFormOutput,
  commonEnumOptionFiltering,
  filterActiveEmploymentSalary,
  transformProposalRequestProps,
} from '~/scenes/RewardingModule/components/EmployeeProposalsDialog/components/EmployeeProposal/components/Forms/Common';
import { useCommonFieldProps } from '~/scenes/RewardingModule/components/EmployeeProposalsDialog/components/EmployeeProposal/components/Forms/Common/useCommonFieldProps';
import { useInitiateFormData } from '~/scenes/RewardingModule/components/EmployeeProposalsDialog/components/EmployeeProposal/components/Forms/Common/useInitiateFormData';
import { useProposalPermissions } from '~/scenes/RewardingModule/components/EmployeeProposalsDialog/components/EmployeeProposal/components/Forms/Common/useProposalPermissions';
import {
  SalaryProposalFieldProps,
  SalaryProposalFormProps,
} from '~/scenes/RewardingModule/components/EmployeeProposalsDialog/components/EmployeeProposals/components/EmployeeSalaryProposalsLayout/useSalaryProposalsLayout';
import { getEmployeeAttachmentUrl } from '~/utils/helper';

import { useEmploymentEnumsFilter } from '../Common/useEmploymentEnumsFilter';
import { useScheduleEmployments } from '../Common/useScheduleEmployments';

import {
  CurrentEmploymentSalary,
  SalaryProposalFormData,
  mapDefaultData,
  mapFormDataToLegacyPayload,
  submitSalaryProposal,
  useValidationSchema,
} from './data';

type PropsType = {
  proposal: SalaryProposalProps;
  employeeId: number;
  onProposalUpdate: EmployeeProposalUpdateCallback;
  formProps: SalaryProposalFormProps;
};

type UseSalaryProposalFormOutput =
  UseCommonProposalFormOutput<SalaryProposalFormData> & {
    enums: {
      eventFrequencies: Enum[];
      salaryTypes: Enum[];
      salaryClasses: Enum[];
      changeReasons: Enum[];
      hourlySalaryComponents: Enum[];
    };
    currentEmploymentSalary: CurrentEmploymentSalary;
    showHourlySalaryComponents: boolean;
    isLoading: {
      applicationConfig: boolean;
    };
    fieldProps: SalaryProposalFieldProps & CommonProposalFieldProps;
    attachmentUrl: string | undefined;
  };

export const useSalaryProposalForm = (
  props: PropsType,
): UseSalaryProposalFormOutput => {
  const { proposal, employeeId, onProposalUpdate, formProps } = props;
  const { proposalData } = proposal;
  const { currentEmploymentSalary } = formProps;

  const { filterActiveEnums } = useEmploymentEnumsFilter();
  const {
    readOnlyMode,
    userRoles,
    loggedInEmployeeId,
    canSubmitForm,
    responsibleHRSelectEnabled,
  } = useProposalPermissions({ proposal });

  const { applicationConfig, isLoading: isLoadingApplicationConfig } =
    useApplicationConfig();
  const enableHourlySalaryComponents =
    applicationConfig.fComConfig.miscellaneousConfig
      .fComEnableHourlySalaryComponents;

  const { getEnums } = useAllEnums();
  const enums = useMemo(() => {
    const { filterEnumsByCodes, rejectCodes } = commonEnumOptionFiltering;

    const getEnumOptions = (
      listKey: EnumListKey,
      selectedValue?: string,
    ): Enum[] => filterActiveEnums(getEnums(listKey), selectedValue);

    return {
      eventFrequencies: filterEnumsByCodes(
        getEnumOptions(EnumListKey.EVENT_FREQUENCY, proposalData?.frequencyFK),
        rejectCodes.EVENT_FREQUENCY,
      ),
      salaryTypes: getEnumOptions(
        EnumListKey.REW_SALARY_TYPE,
        proposalData?.proposedSalaryTypeFK,
      ),

      salaryClasses: getEnumOptions(
        EnumListKey.REW_SALARY_CLASS,
        proposalData?.proposedSalaryClassFK,
      ),

      changeReasons: filterEnumsByCodes(
        getEnumOptions(
          EnumListKey.SALARY_CHANGE_REASON,
          proposalData?.reasonFK,
        ),
        ['HR_EDIT'],
      ),

      hourlySalaryComponents: getEnums(EnumListKey.HOURLY_SALARY_COMPONENT),
    };
  }, [
    proposalData?.frequencyFK,
    proposalData?.proposedSalaryTypeFK,
    proposalData?.proposedSalaryClassFK,
    proposalData?.reasonFK,
    getEnums,
    filterActiveEnums,
  ]);

  const dataMapper = useCallback(
    (
      targetEmploymentId: number,
      proposalData?: SalaryProposalData,
      currentResponsibleHRId?: number,
    ) => {
      const initialActiveSalary = filterActiveEmploymentSalary(
        targetEmploymentId,
        currentEmploymentSalary,
      );
      return mapDefaultData(
        targetEmploymentId,
        initialActiveSalary,
        enums.hourlySalaryComponents,
        enableHourlySalaryComponents,
        proposalData,
        currentResponsibleHRId,
      );
    },
    [
      currentEmploymentSalary,
      enums.hourlySalaryComponents,
      enableHourlySalaryComponents,
    ],
  );

  const { hasUpcomingEmployments } = useScheduleEmployments();

  const resolver = useYupValidationResolver(
    useValidationSchema({
      enableHourlySalaryComponents,
      currentEmploymentSalary,
      filterActiveEmploymentSalary,
      hasUpcomingEmployments,
    }),
    'react-hook-form',
  );

  const {
    isLoading,
    allResponsibleHRIds,
    form,
    handleFormReset,
    handleSubmitRequest,
    quickFixEnsureAttachmentFileInPayload,
  } = useInitiateFormData<SalaryProposalProps, SalaryProposalFormData>({
    proposal,
    employeeId,
    dataMapper,
    resolver,
  });

  const { watch, getValues } = form;

  const employmentId = getValues('employmentId');
  const previousEmploymentSalary = useMemo(() => {
    return filterActiveEmploymentSalary(+employmentId, currentEmploymentSalary);
  }, [currentEmploymentSalary, employmentId]);

  const handleFormSubmit = useCallback(
    (decision?: EmployeeProposalDecision) => {
      const attachmentFile = getValues('attachment');

      return handleSubmitRequest({
        onSuccess: onProposalUpdate,
        handleSubmission: (data, requestProps) => {
          requestProps = transformProposalRequestProps(
            requestProps,
            +data.employmentId,
          );
          return submitSalaryProposal(
            mapFormDataToLegacyPayload({
              data: quickFixEnsureAttachmentFileInPayload(data, attachmentFile),
              isHRUser: userRoles.isHR,
              loggedInEmployeeId,
              decision,
              currentEmploymentSalary,
              enableHourlySalaryComponents,
              requestProps,
            }),
            requestProps,
          );
        },
      });
    },
    [
      handleSubmitRequest,
      onProposalUpdate,
      userRoles,
      loggedInEmployeeId,
      currentEmploymentSalary,
      enableHourlySalaryComponents,
      quickFixEnsureAttachmentFileInPayload,
      getValues,
    ],
  );

  const frequency = watch('salaryFrequency');
  const showHourlySalaryComponents =
    frequency === 'HOURLY' && enableHourlySalaryComponents;

  const proposalFieldProps =
    proposal.proposalStatus === ProposalStatus.PROPOSED
      ? formProps.newProposal
      : formProps.pendingProposal;

  const { isLoading: isLoadingCommonFieldProps, ...fieldProps } =
    useCommonFieldProps({
      startDate: {
        value: watch('startDate'),
        minDate: proposalFieldProps.datePickers.startDate.minDate,
      },
      endDate: { value: watch('endDate') },
      employmentId: watch('employmentId'),
    });

  const attachmentId = watch('attachmentId');
  const attachmentUrl = useMemo(() => {
    return !!attachmentId && attachmentId > 0
      ? getEmployeeAttachmentUrl(employeeId, attachmentId)
      : undefined;
  }, [employeeId, attachmentId]);

  return {
    form,
    handleFormSubmit,
    handleFormReset,
    enums,
    responsibleHRSelector: {
      options: allResponsibleHRIds,
      isResponsibleHR: userRoles.isResponsibleHR,
      enabled: responsibleHRSelectEnabled,
    },
    isLoading: {
      responsibleHR: isLoading,
      applicationConfig: isLoadingApplicationConfig,
      commonFieldProps: isLoadingCommonFieldProps,
    },
    currentEmploymentSalary: previousEmploymentSalary,
    showHourlySalaryComponents,
    readOnlyMode,
    canSubmitForm,
    fieldProps,
    attachmentUrl,
  };
};
